import logger from 'js/app/loggerSingleton';

import inServerContext from 'bundles/ssr/util/inServerContext';

import * as fallbackArticleMappings from '../zendeskToSalesforceArticleMappings.json';
import type { ZendeskHelpCenterSubdomainValue, ZendeskToSalesforcePageMappingsCollection } from './types';

// IMPORTANT local development note: By default, caching will not work when running in local development and it will always return the fallback mappings.
// To make local dev reflective of production node.js module and globals caching, go to /lib/WebpackRouter.js and comment out the 'X-Coursera-Dev-Purge-Module-Cache': 'true' and 'X-Coursera-Dev-Disable-Template-Cache': 'true' lines (don't commit this!).  You'll then have to restart yarn serve prod between certain code changes.

// Note: There is a Retool app for updating the mappings file at https://retool.coursera.org/apps/Help%20and%20Support/Help%20Article%20Mappings
const remoteHelpArticleMappingsUrl =
  'https://coursera-internal-tools-url-mappings.s3.amazonaws.com/help_articles_mappings.json';
const mappingsCacheTtlMillisec = 60 * 1000; // 60 sec.  As of Nov 2021, we have approx 30 calls per minute

const cachedMappingsInfo = {
  lastUpdated: 0, // epoch millisec timestamp.  initialized to 0 so that the fallback mappings are always considered stale
  mappings: fallbackArticleMappings as ZendeskToSalesforcePageMappingsCollection, // fall back to local mappings that require a PR to update
};

export const getCachedMappingsInfo = () => {
  // Because cachedMappingsInfo is defined in the top-level scope of this module, it will be the same shared object instance across SSR requests due to node.js module caching (but see local dev note above).
  // If there is an SSR failure on the current request, then this code is running in the browser with a fresh instance of the cachedMappingsInfo object, which never had the fallback mappings replaced.  The fallback mappings could be quite out of date (but better than nothing)

  if (!inServerContext) {
    logger.error(
      'Falling back to local help article mappings file because we are in CSR instead of SSR.  Check logs for a corresponding SSR failure.'
    );
  }

  return cachedMappingsInfo;
};

export const updateCachedMappings = (mappings: ZendeskToSalesforcePageMappingsCollection) => {
  cachedMappingsInfo.mappings = mappings;
  cachedMappingsInfo.lastUpdated = new Date().getTime();
};

export const isRemoteMappingsOk = (mappings?: ZendeskToSalesforcePageMappingsCollection) => {
  if (!mappings) {
    return false;
  }

  const helpCenters: ZendeskHelpCenterSubdomainValue[] = ['learner', 'partner', 'business'];
  for (const helpCenter of helpCenters) {
    const hcMappings = mappings[helpCenter];
    if (!hcMappings || !hcMappings.article || !hcMappings.section) {
      return false;
      // Note: not requiring category to be present because there are few of those, and we might do away with the category/section distinction in the future?
    }
    // Expect at least 5+ articles and 1+ sections in every help center.  Anything less and there is a really good chance that this is a test mapping file that made it's way to prod, or the tool for updating accidentally overwrote a whole section, or some other problem happened.
    if (Object.keys(hcMappings.article).length < 5 || Object.keys(hcMappings.section).length < 1) {
      return false;
    }
  }

  return true;
};

export const refreshMappings = async () => {
  logger.info('Refreshing help article mappings from remote URL...');
  // Potential performance improvement: We can store an etag or last modified date and use If-None-Match or If-Modified-Since on the request to avoid downloading the full file if it has not changed
  const response = await fetch(remoteHelpArticleMappingsUrl);
  const { data } = await response.json();

  // Sanity check that we got the expected data before updating the mappings in case of a corrupted file, things getting overridden with test mappings, etc.
  if (!isRemoteMappingsOk(data)) {
    logger.error(
      'Remote help article mappings appear to be invalid or corrupted!  Using the fallback mappings instead.  Please check ' +
        remoteHelpArticleMappingsUrl
    );
    return;
  }

  updateCachedMappings(data);
};

export const refreshMappingsInBackgroundIfStale = () => {
  if (!inServerContext) {
    // We could be here in case of SSR failure, in which case this code is running in the browser.
    // There is no point background refreshing the latest mappings from the server if we are in the browser -- at best we could make it cache in the browser only for this 1 user, who is unlikely to make another help article page request anytime soon.
    // IMPORTANT: If SSR fails this means it will always use the local fallback mappings in this repo (imported above), which will be potentially very out of date (although better than nothing)
    // We could await a mappings download on every request where SSR failed, but I don't think we should do that because (a) SSR failure is a rare exception scenario that is not supposed to happen and (b) it would require a big effort to make this change (all existing code and tests assume that mappings retrieval is synchronous instead of returning a promise).
    // If SSR failure happens more than we'd like, we should try to investigate and fix that.  Failing that, we could periodically manually update the fallback hardcoded mappings that are imported at the top of this file.

    // We already receive a Sentry alert when SSR failure occurs and a client-side redirection is performed instead, so no need for additional logging here.

    return;
  }

  const isCachedMappingsStale = new Date().getTime() - cachedMappingsInfo.lastUpdated >= mappingsCacheTtlMillisec;
  if (isCachedMappingsStale) {
    refreshMappings();
  }
};
